import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { API_URL } from '../../app.config';
import { StorageService } from '../storage/storage.service';
import { PwaService } from '../pwa/pwa.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import it_translation from 'src/assets/i18n/it.json';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private APP_KEY: string | null = null;
  private errors: any = null;

  constructor(
    private pwaService: PwaService,
    private translocoService: TranslocoService,
    private route: ActivatedRoute,
    private http: HttpClient,
    private storageService: StorageService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject('message') public message: string
  ) {
    this.errors = it_translation.api_error;

    if (!isPlatformBrowser(this.platformId)) {
      this.APP_KEY = this.message;
    }
    this.translocoService
      .selectTranslateObject('api_error')
      .subscribe((translations) => (this.errors = translations));
  }

  private _handleErrors(error: HttpErrorResponse): Observable<any> {
    let errorMessage: string;
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error.status) {
      if (this.errors.statuses['code-' + error.status]) {
        errorMessage = this.errors.statuses['code-' + error.status];
      } else {
        errorMessage = this.errors.general_with_code.replace(
          '%code%',
          error.status
        );
      }
    } else errorMessage = this.errors.general;
    return throwError(() => errorMessage);
  }

  /**
   * Send a request to register an Insight event
   * @param payload insight event payload
   * @returns an Observable with the result of the request
   */
  sendRating(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(API_URL + `/sendrating`, JSON.stringify(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Send a request to register an Insight event
   * @param payload insight event payload
   * @returns an Observable with the result of the request
   */
  sendInsight(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(API_URL + `/sendinsight`, JSON.stringify(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Get company information
   * @returns an Observable with the result of the request
   */
  getCompany(): Observable<any> {
    if (
      typeof localStorage !== 'undefined' &&
      this.storageService.getAppKey()?.length != 32
    ) {
      // user does not have an APP KEY in the cookies, show error page
      this.router.navigate([this.storageService.getUidPrefix() + '/error'], {
        queryParams: { code: 'APP_KEY_NOT_FOUND' },
      });
      return new Observable<any>((observer) => {
        observer.complete();
      });
    }

    return new Observable<any>((observer) => {
      this.route.queryParams.subscribe((params) => {
        let requestParams = {};
        if (params['reload']) {
          //bypass cache
          requestParams = { timestamp: Date.now() };
          console.log('Bypass cache for /popolapwaguest');
        }

        this.http
          .get<any>(
            API_URL +
              `/popolapwaguest/${
                this.APP_KEY || this.storageService.getAppKey()
              }`,
            {
              params: requestParams,
              headers: {
                'Content-Type': 'application/json',
              },
            }
          )
          .pipe(catchError((error) => this._handleErrors(error)))
          .subscribe({
            next: (response) => {
              if (!response.success || response.error) {
                observer.error(this.errors.response_general);
              } else {
                if (response.data.active) {
                  observer.next(response);
                  observer.complete();
                } else {
                  this.router.navigate([this.storageService.getUidPrefix() + '/error'], {
                    queryParams: { code: 'PWA_SUSPENDED' },
                  });
                  observer.error(this.errors.company_not_active);
                  observer.complete();
                }
              }
            },
            error: (error) => {
              observer.error(error);
              observer.complete();
            },
          });
      });
    });
  }

  /**
   * Get user data information
   * @returns an Observable with the result of the request
   */
  getMetadata(): Observable<any> {
    return new Observable<any>((observer) => {
      this.route.queryParams.subscribe((params) => {
        let requestParams = {};
        if (params['reload']) {
          //bypass cache
          requestParams = { timestamp: Date.now() };
          console.log('Bypass cache for /myapp');
        }

        this.http
          .post<any>(
            API_URL + `/metadata`,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          )
          .pipe(catchError((error) => this._handleErrors(error)))
          .subscribe({
            next: (response) => {
              if (!response.success || response.error) {
                observer.error(this.errors.response_general);
              } else {
                observer.next(response);
                observer.complete();
              }
            },
            error: (error) => {
              observer.error(error);
              observer.complete();
            },
          });
      });
    });
  }

  /**
   * Get user data information
   * @param uid data to send to the server
   * @returns an Observable with the result of the request
   */
  getMyApp(uid: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.route.queryParams.subscribe((params) => {
        let requestParams = {};
        if (params['reload']) {
          //bypass cache
          requestParams = { timestamp: Date.now() };
          console.log('Bypass cache for /myapp');
        }

        this.http
          .post<any>(
            API_URL + `/myapp`,
            { uid: uid },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          )
          .pipe(catchError((error) => this._handleErrors(error)))
          .subscribe({
            next: (response) => {
              if (!response.success || response.error) {
                observer.error(this.errors.response_general);
              } else {
                observer.next(response);
                observer.complete();
              }
            },
            error: (error) => {
              observer.error(error);
              observer.complete();
            },
          });
      });
    });
  }

  /**
   * Used to update the notification status of the user.
   * @param payload data to send to the server
   * @returns an Observable with the result of the request
   */
  updateNotificationState(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(API_URL + `/updatestate`, JSON.stringify(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Update FCM token for the user
   * @param payload payload to send to the server
   * @returns an Observable with the result of the request
   */
  updateNotificationToken(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(API_URL + `/updatepush`, JSON.stringify(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Get the user identifier for the specified user.
   * @returns an Observable with the result of the request
   */
  getUserUID(): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .get<string>(API_URL + `/newuid`, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Send a request to register a support request
   * @param payload payload to send to the server
   * @returns an Observable with the result of the request
   */
  sendSupportRequest(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(API_URL + `/supportrequest`, JSON.stringify(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Send a contact form to the company
   * @param payload payload to send to the server
   * @returns an Observable with the result of the request
   */
  contactCompany(payload: any): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(
          API_URL + `/contactcompany`,
          JSON.stringify({
            ...payload,
            appkey: this.storageService.getAppKey(),
          }),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  sendWelcomePushNotification(token: string): Observable<any> {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(
          API_URL + `/sendpushwelcome`,
          JSON.stringify({
            token: token,
            appkey: this.storageService.getUID(),
          }),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }

  /**
   * Update lang for the client
   * @param lang lang code
   * @returns an Observable with the result of the request
   */
  updateLang(uid: string, lang: string) {
    return new Observable<any>((observer) => {
      this.http
        .post<any>(
          API_URL + `/updatelang`,
          JSON.stringify({
            lang,
            uid,
            appkey: this.storageService.getAppKey(),
          }),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .pipe(catchError((error) => this._handleErrors(error)))
        .subscribe({
          next: (response) => {
            if (!response.success || response.error) {
              observer.error(this.errors.response_general);
            } else {
              observer.next(response);
            }
          },
          error: (error) => observer.error(error),
        });
    });
  }
}
